import { StatusBadge } from '@chiroup/components/StatusBadge';
import { SeverityEnum } from '@chiroup/components/enums/severity';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import {
  BILLING_READINESS_WINDOWS,
  BillingReadinessRow,
  BillingReadinessSeverity,
  BillingReadinessWindow,
  DEFAULT_BILLING_READINESS_WINDOW,
} from '@chiroup/core/types/BillingReadiness.type';
import React, { useState } from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useBillingReadiness from '../../hooks/useBillingReadiness';
import LoadingPage from '../common/LoadingPage';
import Header from '../layout/Header';

const SEVERITY_META: Record<BillingReadinessSeverity, { severity: SeverityEnum; label: string }> = {
  [BillingReadinessSeverity.Critical]: { severity: SeverityEnum.Error, label: 'Critical' },
  [BillingReadinessSeverity.Moderate]: { severity: SeverityEnum.Warning, label: 'Moderate' },
  [BillingReadinessSeverity.Normal]: { severity: SeverityEnum.Success, label: 'Normal' },
  [BillingReadinessSeverity.None]: { severity: SeverityEnum.Note, label: '—' },
};

/** Numeric rank so columns sort by urgency (Critical first) regardless of label. */
const severityRank: Record<BillingReadinessSeverity, number> = {
  [BillingReadinessSeverity.Critical]: 3,
  [BillingReadinessSeverity.Moderate]: 2,
  [BillingReadinessSeverity.Normal]: 1,
  [BillingReadinessSeverity.None]: 0,
};

const SignalBadge: React.FC<{ severity: BillingReadinessSeverity; text: string }> = ({
  severity,
  text,
}) => {
  const meta = SEVERITY_META[severity];
  return <StatusBadge severity={meta.severity} status={text} />;
};

const WINDOW_LABELS: Record<BillingReadinessWindow, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  60: 'Last 60 days',
  90: 'Last 90 days',
};

const columns: ColumnDef<BillingReadinessRow>[] = [
  {
    accessorKey: 'clinicName',
    header: 'Clinic',
    cell: ({ row }) => (
      <div className="font-medium text-gray-900 dark:text-white">
        {row.original.clinicName}
        <span className="ml-2 text-xs text-gray-400">#{row.original.clinicId}</span>
      </div>
    ),
  },
  {
    id: 'transmission',
    header: 'Claim transmission',
    accessorFn: row => severityRank[row.transmission.severity],
    cell: ({ row }) => {
      const { businessDaysSince, severity } = row.original.transmission;
      const text = businessDaysSince == null ? 'No history' : `${businessDaysSince} business days`;
      return <SignalBadge severity={severity} text={text} />;
    },
  },
  {
    id: 'rejection',
    header: 'Rejection ratio',
    accessorFn: row => severityRank[row.rejection.severity],
    cell: ({ row }) => {
      const { available, numerator, denominator, pct, severity } = row.original.rejection;
      if (!available) return <span className="text-xs text-gray-400">Not available</span>;
      if (pct == null) return <span className="text-xs text-gray-400">No data</span>;
      return <SignalBadge severity={severity} text={`${numerator}/${denominator} = ${pct}%`} />;
    },
  },
  {
    id: 'denial',
    header: 'Denial ratio',
    accessorFn: row => severityRank[row.denial.severity],
    cell: ({ row }) => {
      const { numerator, denominator, pct, severity } = row.original.denial;
      if (pct == null) return <span className="text-xs text-gray-400">No ERA data</span>;
      return <SignalBadge severity={severity} text={`${numerator}/${denominator} = ${pct}%`} />;
    },
  },
  {
    id: 'lastPayment',
    header: 'Last posted payment',
    accessorFn: row => row.lastPayment.daysSince ?? -1,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastPayment;
      if (daysSince == null) {
        return <span className="text-xs text-gray-400">No payment history</span>;
      }
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
  {
    id: 'lastEra',
    header: 'Last ERA received',
    accessorFn: row => row.lastEra.daysSince ?? -1,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastEra;
      if (daysSince == null) return <span className="text-xs text-gray-400">No ERA history</span>;
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
];

const BillingReadinessReport: React.FC = () => {
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('analytics.view');
  const [window, setWindow] = useState<BillingReadinessWindow>(DEFAULT_BILLING_READINESS_WINDOW);
  const { data, isFetching } = useBillingReadiness(window, canView);

  if (permissionsLoading) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <>
        <Header title="Billing Readiness" />
        <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
          You do not have permission to view the billing readiness report.
        </div>
      </>
    );
  }

  const windowButtons = (
    <div className="mt-4 flex gap-2">
      {BILLING_READINESS_WINDOWS.map(value => (
        <button
          key={value}
          onClick={() => setWindow(value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
            window === value
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-dark-gray-800 dark:text-dark-gray-200 dark:border-dark-gray-600'
          }`}
        >
          {WINDOW_LABELS[value]}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Header title="Billing Readiness">{windowButtons}</Header>
      <div className="p-6">
        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          fetching={isFetching}
          pagination={{ pageSize: 25 }}
        />
      </div>
    </>
  );
};

export default BillingReadinessReport;
